import { useState, useRef, useEffect } from "react";
import { Routes, Route, useParams, Outlet } from "react-router-dom";
import { AppNavLink, AppNavigate } from "./lib/route-primitives";
import { useTranslation } from "react-i18next";
import { cn } from "./lib/utils";
import { WeddingProvider, useWedding } from "./contexts/wedding-context";
import { paths } from "./lib/paths";
import {
  ADMIN_GLOBAL_ROUTES,
  ADMIN_WEDDING_ROUTES,
  ADMIN_LEGACY_REDIRECTS,
  isAdminNavRoute,
} from "./lib/nav-registry";
import { buildRouteElements } from "./lib/build-routes";
import { RouteErrorBoundary } from "./components/route-error-boundary";
import { SUPPORTED_LANGUAGES, type RoutePath } from "@wedding/shared";
import { MobileBottomNav } from "@wedding/ui";
import {
  ArrowLeft, Loader2, LogOut, ShieldCheck, Moon, Sun, Globe, ChevronRight, Check,
} from "lucide-react";
import { AdminAuthProvider, useAdminAuth } from "./contexts/admin-auth-context";
import { AdminLoginPage } from "./pages/login";

// ── Theme toggle ───────────────────────────────────────────────

function ThemeToggle() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  function toggle() {
    const next = !isDark;
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
    setIsDark(next);
  }
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
      className={cn(
        "relative h-6 w-11 rounded-full border-2 transition-all duration-300 cursor-pointer shrink-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:opacity-80",
        isDark ? "bg-primary/30 border-primary/50" : "bg-amber-200 border-amber-300"
      )}
    >
      <span className={cn(
        "absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center",
        isDark ? "translate-x-[18px] bg-primary" : "translate-x-0.5 bg-amber-400"
      )}>
        {isDark ? <Moon className="h-2.5 w-2.5 text-white" /> : <Sun className="h-2.5 w-2.5 text-white" />}
      </span>
    </button>
  );
}

// ── Admin User Menu ────────────────────────────────────────────

function AdminUserMenu() {
  const { t, i18n } = useTranslation();
  const { email, logout } = useAdminAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"main" | "language">("main");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setView("main");
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen]);

  const initial = (email ?? "A")[0].toUpperCase();
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setIsOpen((v) => !v); setView("main"); }}
        aria-label="Kullanıcı menüsü"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="relative h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-primary to-primary/70 text-white text-sm font-bold flex items-center justify-center ring-2 ring-primary/30 ring-offset-2 ring-offset-background hover:ring-primary/50 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {initial}
        <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-amber-500 border border-background flex items-center justify-center">
          <ShieldCheck className="h-2 w-2 text-white" />
        </span>
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 bottom-full mb-2 min-w-56 rounded-2xl border border-primary/10 bg-background/95 backdrop-blur-xl shadow-lg z-50 overflow-hidden md:bottom-auto md:top-full md:mb-0 md:mt-2"
        >
          {view === "main" ? (
            <>
              <div className="px-4 py-3 border-b border-primary/10">
                <div className="flex items-center gap-1.5 mb-1">
                  <ShieldCheck className="h-3 w-3 text-amber-500 shrink-0" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500">Platform Admin</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{email}</p>
              </div>
              <div className="p-1.5 space-y-0.5">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setView("language")}
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-left">{currentLang.label}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              </div>
              <div className="border-t border-primary/10 p-1.5">
                <button
                  onClick={() => { setIsOpen(false); void logout(); }}
                  role="menuitem"
                  className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors cursor-pointer"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  {t("common.logout")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-3 border-b border-primary/10">
                <button
                  type="button"
                  onClick={() => setView("main")}
                  className="p-1 rounded-md hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium">{t("common.language")}</span>
              </div>
              <div className="p-1.5 space-y-0.5">
                {SUPPORTED_LANGUAGES.map((lang) => {
                  const active = lang.code === i18n.language;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      role="menuitem"
                      onClick={() => { void i18n.changeLanguage(lang.code); setView("main"); }}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer",
                        active ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-primary/5"
                      )}
                    >
                      <Check className={cn("h-3.5 w-3.5 shrink-0", active ? "opacity-100" : "opacity-0")} />
                      {lang.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Nav item types ─────────────────────────────────────────────

type NavItem = {
  /** Marka tipi burada da korunur — `MobileNavItem.to` ve `AppNavLink`
   *  sınırında düz `string`e düşmesin diye. */
  to: RoutePath;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  end?: boolean;
};

// ── Shared sidebar ─────────────────────────────────────────────

function Sidebar({ navItems, brand }: { navItems: NavItem[]; brand: React.ReactNode }) {
  return (
    <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-primary/10 bg-background sticky top-0 h-screen overflow-y-auto">
      <div className="px-6 py-6 border-b border-primary/10">
        {brand}
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <AppNavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
              )
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {item.label}
          </AppNavLink>
        ))}
      </nav>
    </aside>
  );
}

// ── Shared mobile header ───────────────────────────────────────

function MobileHeader({ left, title }: { left?: React.ReactNode; title: string }) {
  return (
    <header className="glass fixed top-0 inset-x-0 z-50 h-16 border-b border-primary/10 md:hidden">
      <div className="flex items-center h-full px-4 gap-2">
        {left}
        <span className="flex-1 font-serif italic text-lg text-primary truncate">{title}</span>
        <ThemeToggle />
        <AdminUserMenu />
      </div>
    </header>
  );
}

// ── Admin Global Layout ────────────────────────────────────────

function AdminGlobalLayout() {
  const { t } = useTranslation();
  const navItems: NavItem[] = ADMIN_GLOBAL_ROUTES.filter(isAdminNavRoute).map((r) => ({
    to: r.to,
    icon: r.icon,
    label: t(r.labelKey),
    end: r.end,
  }));

  const brand = (
    <AppNavLink to={paths.root} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
      <div className="h-8 w-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
        <ShieldCheck className="h-4 w-4 text-primary" />
      </div>
      <span className="font-serif italic text-xl text-primary">Vite</span>
    </AppNavLink>
  );

  return (
    <div className="relative min-h-screen md:flex md:overflow-hidden md:h-screen">
      <Sidebar navItems={navItems} brand={brand} />
      <MobileHeader title="Vite" />
      <div className="flex-1 flex flex-col md:overflow-y-auto md:h-screen">
        <header className="hidden md:flex sticky top-0 z-10 items-center justify-end px-8 py-4 bg-background border-b border-primary/10 shrink-0 gap-2">
          <ThemeToggle />
          <AdminUserMenu />
        </header>
        <main className="flex-1 px-4 pt-20 pb-28 md:px-8 md:py-8 overflow-x-clip">
          <div className="mx-auto w-full max-w-7xl">
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>
      <MobileBottomNav navItems={navItems} />
    </div>
  );
}

// ── Wedding Layout ─────────────────────────────────────────────

function WeddingLayout() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const navItems: NavItem[] = ADMIN_WEDDING_ROUTES.map((r) => ({
    to: r.to(slug ?? ""),
    icon: r.icon,
    label: t(r.labelKey),
    end: r.end,
  }));

  return (
    <WeddingProvider>
      <WeddingLayoutInner navItems={navItems} />
    </WeddingProvider>
  );
}

function WeddingLayoutInner({ navItems }: { navItems: NavItem[] }) {
  const { t } = useTranslation();
  const { wedding, isLoading, error } = useWedding();

  if (isLoading && !wedding) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const weddingTitle = wedding?.title ?? t("nav.dashboard");

  const brand = (
    <div>
      <AppNavLink
        to={paths.root}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors mb-3"
      >
        <ArrowLeft className="h-3 w-3" />
        {t("nav.allWeddings")}
      </AppNavLink>
      <p className="font-serif text-xl font-bold text-foreground leading-snug truncate">
        {weddingTitle}
      </p>
    </div>
  );

  return (
    <div className="relative min-h-screen md:flex md:overflow-hidden md:h-screen">
      <Sidebar navItems={navItems} brand={brand} />
      <MobileHeader
        left={
          <AppNavLink
            to={paths.root}
            className="p-2 text-muted-foreground hover:text-primary transition-colors shrink-0"
            aria-label={t("nav.allWeddings")}
          >
            <ArrowLeft className="h-5 w-5" />
          </AppNavLink>
        }
        title={weddingTitle}
      />
      <div className="flex-1 flex flex-col md:overflow-y-auto md:h-screen">
        <header className="hidden md:flex sticky top-0 z-10 items-center justify-end px-8 py-4 bg-background border-b border-primary/10 shrink-0 gap-2">
          <ThemeToggle />
          <AdminUserMenu />
        </header>
        <main className="flex-1 px-4 pt-20 pb-28 md:px-8 md:py-8 overflow-x-clip">
          <div className="mx-auto w-full max-w-7xl">
            {error ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <h2 className="text-xl font-bold">{t("wedding.notFound")}</h2>
                <p className="text-muted-foreground">{error}</p>
                <AppNavLink
                  to={paths.root}
                  className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-4 py-2 text-sm font-medium hover:bg-primary/5 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("admin.backToWeddings")}
                </AppNavLink>
              </div>
            ) : (
              <RouteErrorBoundary>
                <Routes>
                  {buildRouteElements(ADMIN_WEDDING_ROUTES, (r) => <r.element />)}
                  <Route path="*" element={<AppNavigate to={paths.self} replace />} />
                </Routes>
              </RouteErrorBoundary>
            )}
          </div>
        </main>
      </div>
      <MobileBottomNav navItems={navItems} />
    </div>
  );
}

// ── Authenticated app ──────────────────────────────────────────

function AuthenticatedApp() {
  return (
    <Routes>
      <Route element={<AdminGlobalLayout />}>
        {buildRouteElements(ADMIN_GLOBAL_ROUTES, (r) => <r.element />)}
        {/* Eski yol yönlendirmeleri — 2026-07-27'den beri liste BOŞ, yani
            pratikte hiçbir yönlendirme yok (bkz. nav-registry.ts). Mount
            noktası duruyor ki gerçekten gerekirse tek satırla geri gelsin ve
            route-resolution testi onu anında kapsasın. */}
        {ADMIN_LEGACY_REDIRECTS.map(({ from, to }) => (
          <Route key={from} path={from} element={<AppNavigate to={to} replace />} />
        ))}
      </Route>
      <Route path="/:slug/*" element={<WeddingLayout />} />
      <Route path="*" element={<AppNavigate to={paths.root} replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <AdminAuthProvider>
      <AppInner />
    </AdminAuthProvider>
  );
}

function AppInner() {
  const { email, isLoading } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!email) return <AdminLoginPage />;

  return <AuthenticatedApp />;
}
